import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Search, Bell, Plus, Building, ChevronDown, LogOut, Settings, Sparkles, Sun, Moon, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import CommandPalette from "./CommandPalette";

export default function TopBar({ onMobileToggle, onCopilotOpen }) {
    const { user, orgs, activeOrg, switchOrg, logout } = useAuth();
    const navigate = useNavigate();
    const [cmdOpen, setCmdOpen] = useState(false);
    const [notifs, setNotifs] = useState([]);
    useEffect(() => {
        // Theme switcher removed — force light mode
        document.documentElement.classList.remove("dark");
        try { localStorage.removeItem("vyastra_theme"); } catch {} // eslint-disable-line
    }, []);

    useEffect(() => {
        api.get("/notifications").then(({ data }) => setNotifs(data || [])).catch(() => {});
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setCmdOpen(true);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    const unread = notifs.filter((n) => !n.is_read).length;

    return (
        <header className="sticky top-0 z-20 h-[52px] bg-background border-b border-border flex items-center justify-between px-4 md:px-6 gap-3" data-testid="app-topbar">
            <div className="flex items-center gap-3 min-w-0">
                <button data-testid="mobile-menu" className="lg:hidden p-2 -ml-2" onClick={onMobileToggle}>
                    <Menu className="w-5 h-5" />
                </button>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button data-testid="org-switcher" className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-card hover:bg-muted/60 text-[13px]">
                            <Building className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                            <span className="font-medium truncate max-w-[160px]">{activeOrg?.name || "Workspace"}</span>
                            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-[260px]">
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Switch organization</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {orgs.map((o) => (
                            <DropdownMenuItem key={o.id} onClick={() => switchOrg(o.id)} className="flex flex-col items-start gap-0.5 py-2" data-testid={`org-option-${o.id}`}>
                                <span className="font-medium">{o.name}</span>
                                <span className="text-[11px] text-muted-foreground">{o.industry} • {o.base_currency}</span>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <button
                onClick={() => setCmdOpen(true)}
                data-testid="global-search-trigger"
                className="hidden md:flex items-center gap-2 flex-1 max-w-[440px] h-8 px-2.5 border border-border bg-card hover:border-gold/50 text-muted-foreground text-[12px] font-mono"
                style={{ borderRadius: 2 }}
            >
                <Search className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span className="tracking-[0.06em]">{`> search invoices, vendors, items…`}</span>
                <kbd className="ml-auto text-[10px] font-mono bg-background border border-border px-1.5 py-px tracking-wider">⌘K</kbd>
            </button>

            <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={onCopilotOpen} data-testid="topbar-copilot" className="hidden md:flex gap-1.5 h-9 px-3 text-[12px]">
                    <Sparkles className="w-4 h-4 text-gold" strokeWidth={1.5} />
                    <span>Copilot</span>
                </Button>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-9 w-9 relative" data-testid="notifications-btn">
                            <Bell className="w-4 h-4" strokeWidth={1.5} />
                            {unread > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-gold" />}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[340px] max-h-[420px] overflow-y-auto">
                        <DropdownMenuLabel className="flex items-center justify-between">
                            <span>Notifications</span>
                            <span className="text-[11px] text-muted-foreground font-normal">{unread} unread</span>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {notifs.length === 0 ? (
                            <div className="p-4 text-[13px] text-muted-foreground">No notifications.</div>
                        ) : (
                            notifs.map((n) => (
                                <DropdownMenuItem key={n.id} className="flex flex-col items-start gap-1 py-2.5 cursor-pointer" onClick={() => n.link && navigate(n.link)}>
                                    <div className="flex items-center gap-2 w-full">
                                        <span className={`w-1.5 h-1.5 rounded-full ${n.severity === "warning" ? "bg-amber-500" : n.severity === "success" ? "bg-positive" : "bg-foreground"}`} />
                                        <span className="font-medium text-[13px]">{n.title}</span>
                                    </div>
                                    {n.body && <span className="text-[11.5px] text-muted-foreground">{n.body}</span>}
                                </DropdownMenuItem>
                            ))
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button data-testid="user-menu" className="flex items-center gap-2 ml-1 px-2 py-1 rounded-md hover:bg-muted/60">
                            <div className="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center font-medium text-sm">
                                {(user?.name || "?").slice(0, 1).toUpperCase()}
                            </div>
                            <div className="hidden md:flex flex-col items-start leading-tight">
                                <span className="text-[12.5px] font-medium">{user?.name}</span>
                                <span className="text-[10px] text-muted-foreground">{user?.role}</span>
                            </div>
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[200px]">
                        <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="user-menu-settings">
                            <Settings className="w-4 h-4 mr-2" /> Settings
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={logout} data-testid="user-menu-logout">
                            <LogOut className="w-4 h-4 mr-2" /> Sign out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
        </header>
    );
}
